/**
 * Enhanced localStorage manager with quota handling and fallback mechanisms
 */

interface StorageItem {
  data: any;
  timestamp: number;
  size: number;
  key: string;
}

interface StorageStats {
  used: number;
  available: number;
  total: number;
  items: StorageItem[];
}

class LocalStorageManager {
  private fallbackStorage = new Map<string, string>();
  private isLocalStorageAvailable = true;

  constructor() {
    this.checkLocalStorageAvailability();
  }

  /**
   * Check if localStorage is available and working
   */
  private checkLocalStorageAvailability(): void {
    try {
      const testKey = '__localStorage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      this.isLocalStorageAvailable = true;
    } catch (error) {
      console.warn('localStorage is not available, using fallback storage:', error);
      this.isLocalStorageAvailable = false;
    }
  }

  /**
   * Get storage statistics
   */
  getStorageStats(): StorageStats {
    const items: StorageItem[] = [];
    let used = 0;

    if (this.isLocalStorageAvailable) {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          const value = localStorage.getItem(key);
          if (value) {
            const size = new Blob([value]).size;
            used += size;
            
            try {
              const parsed = JSON.parse(value);
              items.push({
                key,
                data: parsed,
                timestamp: parsed.timestamp || 0,
                size
              });
            } catch {
              items.push({
                key,
                data: value,
                timestamp: 0,
                size
              });
            }
          }
        }
      }
    } else {
      this.fallbackStorage.forEach((value, key) => {
        const size = new Blob([value]).size;
        used += size;
        try {
          const parsed = JSON.parse(value);
          items.push({
            key,
            data: parsed,
            timestamp: parsed.timestamp || 0,
            size
          });
        } catch {
          items.push({
            key,
            data: value,
            timestamp: 0,
            size
          });
        }
      });
    }

    // Estimate total localStorage capacity (usually 5-10MB)
    const total = 10 * 1024 * 1024; // 10MB estimate
    const available = total - used;

    return { used, available, total, items };
  }

  /**
   * Clean up old items to free space
   */
  private cleanupOldItems(requiredSpace: number): boolean {
    const stats = this.getStorageStats();
    
    if (stats.available >= requiredSpace) {
      return true;
    }

    // Sort items by timestamp (oldest first)
    const sortedItems = stats.items
      .filter(item => item.key.startsWith('program-'))
      .sort((a, b) => a.timestamp - b.timestamp);

    let freedSpace = 0;
    const itemsToRemove: string[] = [];

    for (const item of sortedItems) {
      // Don't remove very recent items (less than 1 hour old)
      if (Date.now() - item.timestamp < 60 * 60 * 1000) {
        continue;
      }

      itemsToRemove.push(item.key);
      freedSpace += item.size;

      if (freedSpace >= requiredSpace) {
        break;
      }
    }

    // Remove the items
    itemsToRemove.forEach(key => {
      this.removeItem(key);
    });

    console.log(`Cleaned up ${itemsToRemove.length} old items, freed ${freedSpace} bytes`);
    return freedSpace >= requiredSpace;
  }

  /**
   * Set item with quota handling
   */
  setItem(key: string, value: string): boolean {
    const size = new Blob([value]).size;
    
    try {
      if (this.isLocalStorageAvailable) {
        localStorage.setItem(key, value);
        return true;
      } else {
        this.fallbackStorage.set(key, value);
        return true;
      }
    } catch (error) {
      if (error instanceof DOMException && error.code === DOMException.QUOTA_EXCEEDED_ERR) {
        console.warn('localStorage quota exceeded, attempting cleanup...');
        
        // Try to clean up old items
        if (this.cleanupOldItems(size)) {
          try {
            if (this.isLocalStorageAvailable) {
              localStorage.setItem(key, value);
              return true;
            } else {
              this.fallbackStorage.set(key, value);
              return true;
            }
          } catch (retryError) {
            console.error('Failed to save even after cleanup:', retryError);
            // Fall back to in-memory storage
            this.fallbackStorage.set(key, value);
            return false; // Indicate fallback was used
          }
        } else {
          console.error('Could not free enough space for new item');
          // Still try to save in fallback storage
          this.fallbackStorage.set(key, value);
          return false;
        }
      } else {
        console.error('Error saving to localStorage:', error);
        // Fall back to in-memory storage
        this.fallbackStorage.set(key, value);
        return false;
      }
    }
  }

  /**
   * Get item from storage
   */
  getItem(key: string): string | null {
    try {
      if (this.isLocalStorageAvailable) {
        return localStorage.getItem(key);
      } else {
        return this.fallbackStorage.get(key) || null;
      }
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return this.fallbackStorage.get(key) || null;
    }
  }

  /**
   * Remove item from storage
   */
  removeItem(key: string): void {
    try {
      if (this.isLocalStorageAvailable) {
        localStorage.removeItem(key);
      }
      this.fallbackStorage.delete(key);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
      this.fallbackStorage.delete(key);
    }
  }

  /**
   * Check if storage is available
   */
  isAvailable(): boolean {
    return this.isLocalStorageAvailable || this.fallbackStorage.size >= 0;
  }

  /**
   * Get storage type being used
   */
  getStorageType(): 'localStorage' | 'fallback' {
    return this.isLocalStorageAvailable ? 'localStorage' : 'fallback';
  }

  /**
   * Clear all program-related data
   */
  clearProgramData(): void {
    const stats = this.getStorageStats();
    const programKeys = stats.items
      .filter(item => item.key.startsWith('program-'))
      .map(item => item.key);

    programKeys.forEach(key => this.removeItem(key));
    console.log(`Cleared ${programKeys.length} program-related items`);
  }
}

// Export singleton instance
export const storageManager = new LocalStorageManager();
export default storageManager;
